import * as THREE from 'three';
import {
    chooseRevolveOrthogonalDirection,
    computeRevolveContoursCenter,
    computeRevolveMaxContourRadius,
    computeRevolveWorldSizeFromPixels,
    normalizeRevolveAxisDirection,
    projectRevolveDirectionToAxisPlane,
    resolveDraggedRevolveState,
    resolveRevolveSignedAngle,
} from './RevolveAngleDragMath';
import type {
    RevolveAngleDragChangePayload,
    RevolveAngleDragTarget,
} from './RevolveAngleDragTypes';
import { RevolveAngleGizmoObject } from '../../../mesh/feature/revolve/RevolveAngleGizmoObject';
import { RevolveAngleGizmoPartKind } from '../../../mesh/feature/revolve/RevolveAngleGizmoPartKind';
import { RevolveDragGhostObject } from '../../../mesh/feature/revolve/RevolveDragGhostObject';
import { addObject3DToParent } from '../../../utils/scene-object/addObject3DToParent';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/interaction/revolve 层；
 * 2. 负责旋转特征角度手柄的鼠标交互；
 * 3. 该控制器由 Viewer 托管生命周期，不依赖 Vue 或 PropertyManager。
 *
 * 文件职责：
 * 1. 管理角度 gizmo 与 ghost 对象的挂载、更新和释放；
 * 2. 处理 mousedown / mousemove / mouseup 的拖拽状态；
 * 3. 通过回调把角度变更同步给宿主会话。
 */

/** 方向 1 最小角度。 */
const MIN_DIRECTION_1_ANGLE = 1e-4;
/** 最大旋转角。 */
const MAX_ANGLE = Math.PI * 2;
/** 手柄半径像素尺寸。 */
const HANDLE_RADIUS_PIXEL_SIZE = 72;
/** 手柄头像素尺寸。 */
const HANDLE_HEAD_PIXEL_SIZE = 18;

/**
 * 旋转角度拖拽控制器运行时上下文。
 */
export interface RevolveAngleDragRuntimeContext {
    /** 当前场景。 */
    scene: THREE.Scene;
    /** 当前相机。 */
    camera: THREE.Camera;
    /** 当前容器。 */
    container: HTMLElement;
    /** 请求渲染。 */
    requestRender?: () => void;
}

/**
 * 旋转角度拖拽控制器。
 */
export class RevolveAngleDragController {
    /** Raycaster。 */
    private readonly raycaster = new THREE.Raycaster();
    /** 鼠标 NDC。 */
    private readonly mouse = new THREE.Vector2();
    /** 拖拽平面。 */
    private readonly dragPlane = new THREE.Plane();
    /** 拖拽交点复用对象。 */
    private readonly dragPoint = new THREE.Vector3();
    /** 当前运行时上下文。 */
    private context: RevolveAngleDragRuntimeContext | null = null;
    /** 当前目标。 */
    private activeTarget: RevolveAngleDragTarget | null = null;
    /** 当前 gizmo。 */
    private gizmo: RevolveAngleGizmoObject | null = null;
    /** 当前 ghost。 */
    private ghost: RevolveDragGhostObject | null = null;
    /** 原始 cursor。 */
    private previousCursor: string | null = null;
    /** 是否拖拽中。 */
    private isDragging = false;
    /** 当前拖拽部件。 */
    private dragPart: RevolveAngleGizmoPartKind | 'unknown' = 'unknown';
    /** 轴原点。 */
    private axisOrigin = new THREE.Vector3();
    /** 轴中心。 */
    private axisCenter = new THREE.Vector3();
    /** 轴方向。 */
    private axisDirection = new THREE.Vector3(0, 0, 1);
    /** 基准方向。 */
    private baseDirection = new THREE.Vector3(1, 0, 0);
    /** 方向 1 角度。 */
    private direction1Angle = MAX_ANGLE;
    /** 是否反向。 */
    private directionReversed = false;
    /** 是否启用方向 2。 */
    private direction2Enabled = false;
    /** 方向 2 角度。 */
    private direction2Angle = 0;
    /** 角度变更回调。 */
    private onAnglesChange: ((payload: RevolveAngleDragChangePayload) => void) | null = null;

    /**
     * 更新拖拽目标。
     *
     * @param input 更新输入。
     * @returns 是否成功更新。
     */
    public updateTarget(input: {
        context: RevolveAngleDragRuntimeContext;
        target: RevolveAngleDragTarget | null;
        onAnglesChange?: (payload: RevolveAngleDragChangePayload) => void;
    }): boolean {
        this.onAnglesChange = input.onAnglesChange ?? this.onAnglesChange;
        if (!input.target) {
            this.clearTarget();
            return true;
        }
        this.bindContext(input.context);
        this.activeTarget = input.target;
        this.direction1Angle = Math.min(MAX_ANGLE, Math.max(MIN_DIRECTION_1_ANGLE, input.target.direction1Angle));
        this.directionReversed = input.target.directionReversed;
        this.direction2Enabled = input.target.direction2Enabled;
        this.direction2Angle = this.direction2Enabled
            ? Math.min(MAX_ANGLE, Math.max(0, input.target.direction2Angle))
            : 0;
        this.updateBasisFromTarget();
        this.ensureGhost();
        this.ghost?.setContours(input.target.contours);
        this.ghost!.visible = false;
        this.ensureGizmo();
        this.requestRender();
        return true;
    }

    /**
     * 同步当前角度状态。
     *
     * @param target 最新拖拽目标。
     */
    public syncState(target: RevolveAngleDragTarget): void {
        if (!this.activeTarget || !this.context) {
            return;
        }
        this.activeTarget = target;
        this.direction1Angle = Math.min(MAX_ANGLE, Math.max(MIN_DIRECTION_1_ANGLE, target.direction1Angle));
        this.directionReversed = target.directionReversed;
        this.direction2Enabled = target.direction2Enabled;
        this.direction2Angle = this.direction2Enabled ? Math.min(MAX_ANGLE, Math.max(0, target.direction2Angle)) : 0;
        this.updateGizmoTransform();
        this.requestRender();
    }

    /**
     * 停止并释放当前交互。
     */
    public stop(): void {
        this.detachListeners();
        this.clearTarget();
        this.context = null;
        this.previousCursor = null;
        this.onAnglesChange = null;
    }

    /**
     * 绑定运行时上下文。
     *
     * @param context 新上下文。
     */
    private bindContext(context: RevolveAngleDragRuntimeContext): void {
        if (this.context?.container === context.container) {
            this.context = context;
            return;
        }
        this.detachListeners();
        this.context = context;
        this.previousCursor = context.container.style.cursor;
        this.attachListeners();
    }

    /**
     * 更新鼠标 NDC。
     *
     * @param event 鼠标事件。
     */
    private updateMouse(event: MouseEvent): void {
        const container = this.context?.container;
        if (!container) {
            return;
        }
        const rect = container.getBoundingClientRect();
        this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    }

    /**
     * 设置鼠标样式。
     *
     * @param cursor 鼠标样式。
     */
    private setCursor(cursor: string | null): void {
        const container = this.context?.container;
        if (!container) {
            return;
        }
        if (cursor) {
            container.style.cursor = cursor;
            return;
        }
        if (this.previousCursor !== null) {
            container.style.cursor = this.previousCursor;
        }
    }

    /**
     * 从目标更新轴基准。
     */
    private updateBasisFromTarget(): void {
        if (!this.activeTarget) {
            return;
        }
        this.axisOrigin = new THREE.Vector3(
            this.activeTarget.axis.point[0],
            this.activeTarget.axis.point[1],
            this.activeTarget.axis.point[2],
        );
        this.axisDirection = normalizeRevolveAxisDirection(new THREE.Vector3(
            this.activeTarget.axis.direction[0],
            this.activeTarget.axis.direction[1],
            this.activeTarget.axis.direction[2],
        ));
        if (this.directionReversed) {
            this.axisDirection.multiplyScalar(-1);
        }
        const contourCenter = this.activeTarget.contours.length > 0
            ? computeRevolveContoursCenter(this.activeTarget.contours)
            : this.axisOrigin.clone();
        const offsetToCenter = contourCenter.clone().sub(this.axisOrigin);
        const axisOffset = offsetToCenter.dot(this.axisDirection);
        this.axisCenter = this.axisOrigin.clone().addScaledVector(this.axisDirection, axisOffset);

        let radialDirection = contourCenter.clone().sub(this.axisCenter);
        radialDirection.sub(this.axisDirection.clone().multiplyScalar(radialDirection.dot(this.axisDirection)));
        if (radialDirection.lengthSq() <= 1e-12 && this.context?.camera) {
            const cameraOffset = this.context.camera.position.clone().sub(this.axisCenter);
            radialDirection = cameraOffset.sub(
                this.axisDirection.clone().multiplyScalar(cameraOffset.dot(this.axisDirection)),
            );
        }
        this.baseDirection = projectRevolveDirectionToAxisPlane(radialDirection, this.axisDirection);
        if (this.baseDirection.lengthSq() <= 1e-12) {
            this.baseDirection = chooseRevolveOrthogonalDirection(this.axisDirection);
        }
    }

    /**
     * 确保 ghost 对象已创建。
     */
    private ensureGhost(): void {
        const scene = this.context?.scene;
        if (!scene) {
            return;
        }
        if (!this.ghost) {
            this.ghost = new RevolveDragGhostObject();
        addObject3DToParent(this.ghost, scene);
        }
    }

    /**
     * 确保 gizmo 对象已创建。
     */
    private ensureGizmo(): void {
        const scene = this.context?.scene;
        if (!scene) {
            return;
        }
        if (!this.gizmo) {
            this.gizmo = new RevolveAngleGizmoObject();
        addObject3DToParent(this.gizmo, scene);
        }
        this.updateGizmoTransform();
    }

    /**
     * 更新 gizmo 姿态。
     */
    private updateGizmoTransform(): void {
        if (!this.gizmo || !this.context || !this.activeTarget) {
            return;
        }
        this.updateBasisFromTarget();
        this.gizmo.orient({
            center: this.axisCenter,
            axisDirection: this.axisDirection,
            baseDirection: this.baseDirection,
        });

        const minRadius = Math.max(
            1e-4,
            computeRevolveWorldSizeFromPixels(
                this.context.camera,
                this.context.container,
                this.axisCenter,
                HANDLE_RADIUS_PIXEL_SIZE,
            ),
        );
        const contourRadius = computeRevolveMaxContourRadius(
            this.activeTarget.contours,
            this.axisCenter,
            this.axisDirection,
        );
        const radius = Math.max(minRadius, contourRadius + minRadius * 0.4);
        const headSize = Math.max(
            1e-4,
            computeRevolveWorldSizeFromPixels(
                this.context.camera,
                this.context.container,
                this.axisCenter,
                HANDLE_HEAD_PIXEL_SIZE,
            ),
        );
        this.gizmo.updateVisual({
            radius,
            headSize,
            direction1Angle: this.direction1Angle,
            direction2Enabled: this.direction2Enabled,
            direction2Angle: this.direction2Angle,
        });
    }

    /**
     * 解析 gizmo 命中。
     *
     * @param event 鼠标事件。
     * @returns 命中列表。
     */
    private resolveGizmoIntersections(event: MouseEvent): THREE.Intersection[] {
        if (!this.context || !this.gizmo) {
            return [];
        }
        this.updateMouse(event);
        this.raycaster.setFromCamera(this.mouse, this.context.camera);
        return this.raycaster.intersectObjects(this.gizmo.children, true)
            .filter((hit) => this.gizmo?.resolvePartKindFromObject(hit.object) !== 'unknown');
    }

    /**
     * 发送角度变更。
     *
     * @param dragging 是否正在拖拽。
     */
    private emitAnglesChange(dragging: boolean): void {
        this.onAnglesChange?.({
            direction1Angle: this.direction1Angle,
            directionReversed: this.directionReversed,
            direction2Enabled: this.direction2Enabled,
            direction2Angle: this.direction2Angle,
            isDragging: dragging,
        });
    }

    /**
     * 应用拖拽角度。
     *
     * @param signedAngle 有符号角。
     */
    private applyDraggedAngle(signedAngle: number): void {
        const nextState = resolveDraggedRevolveState({
            dragPart: this.dragPart,
            signedAngle,
            direction1Angle: this.direction1Angle,
            directionReversed: this.directionReversed,
            direction2Enabled: this.direction2Enabled,
            direction2Angle: this.direction2Angle,
        });
        this.direction1Angle = nextState.direction1Angle;
        this.directionReversed = nextState.directionReversed;
        this.direction2Enabled = nextState.direction2Enabled;
        this.direction2Angle = nextState.direction2Angle;
        this.emitAnglesChange(true);
    }

    /**
     * 处理鼠标按下。
     *
     * @param event 鼠标事件。
     */
    private readonly handleMouseDown = (event: MouseEvent): void => {
        if (!this.activeTarget || event.button !== 0 || !this.gizmo) {
            return;
        }
        const hits = this.resolveGizmoIntersections(event);
        if (hits.length === 0) {
            return;
        }
        const part = this.gizmo.resolvePartKindFromObject(hits[0]?.object ?? null);
        if (part === 'unknown') {
            return;
        }
        this.dragPart = part;
        this.isDragging = true;
        this.emitAnglesChange(true);
        if (this.ghost) {
            this.ghost.visible = true;
        }
        this.setCursor('grabbing');
        event.preventDefault();
        event.stopPropagation();
    };

    /**
     * 处理鼠标移动。
     *
     * @param event 鼠标事件。
     */
    private readonly handleMouseMove = (event: MouseEvent): void => {
        if (!this.activeTarget || !this.context) {
            return;
        }
        this.updateMouse(event);
        this.raycaster.setFromCamera(this.mouse, this.context.camera);
        this.updateGizmoTransform();

        if (!this.isDragging) {
            const hoverOnHandle = this.resolveGizmoIntersections(event).length > 0;
            this.setCursor(hoverOnHandle ? 'grab' : null);
            this.requestRender();
            return;
        }

        this.dragPlane.setFromNormalAndCoplanarPoint(this.axisDirection, this.axisCenter);
        const hitPoint = this.raycaster.ray.intersectPlane(this.dragPlane, this.dragPoint);
        if (!hitPoint) {
            return;
        }
        const dragDirection = hitPoint.clone().sub(this.axisCenter);
        dragDirection.sub(this.axisDirection.clone().multiplyScalar(dragDirection.dot(this.axisDirection)));
        if (dragDirection.lengthSq() <= 1e-10) {
            return;
        }
        const signedAngle = resolveRevolveSignedAngle(
            this.axisDirection,
            this.baseDirection,
            dragDirection,
        );
        this.applyDraggedAngle(signedAngle);
        const ghostAngle = this.dragPart === RevolveAngleGizmoPartKind.Direction2Head
            ? -this.direction2Angle
            : this.direction1Angle;
        this.ghost?.updateByAngle({
            axisOrigin: this.axisOrigin,
            axisDirection: this.axisDirection,
            angle: ghostAngle,
        });
        this.updateGizmoTransform();
        this.requestRender();
        event.preventDefault();
        event.stopPropagation();
    };

    /**
     * 处理鼠标松开。
     */
    private readonly handleMouseUp = (): void => {
        if (!this.isDragging) {
            return;
        }
        this.isDragging = false;
        this.dragPart = 'unknown';
        if (this.ghost) {
            this.ghost.visible = false;
        }
        this.emitAnglesChange(false);
        this.setCursor(null);
        this.requestRender();
    };

    /**
     * 绑定 DOM 事件。
     */
    private attachListeners(): void {
        const container = this.context?.container;
        if (!container) {
            return;
        }
        container.addEventListener('mousedown', this.handleMouseDown, true);
        container.addEventListener('mousemove', this.handleMouseMove, true);
        container.addEventListener('mouseup', this.handleMouseUp, true);
        window.addEventListener('mouseup', this.handleMouseUp, true);
        window.addEventListener('pointerup', this.handleMouseUp, true);
        window.addEventListener('pointercancel', this.handleMouseUp, true);
        window.addEventListener('blur', this.handleMouseUp, true);
    }

    /**
     * 解绑 DOM 事件。
     */
    private detachListeners(): void {
        const container = this.context?.container;
        if (!container) {
            return;
        }
        container.removeEventListener('mousedown', this.handleMouseDown, true);
        container.removeEventListener('mousemove', this.handleMouseMove, true);
        container.removeEventListener('mouseup', this.handleMouseUp, true);
        window.removeEventListener('mouseup', this.handleMouseUp, true);
        window.removeEventListener('pointerup', this.handleMouseUp, true);
        window.removeEventListener('pointercancel', this.handleMouseUp, true);
        window.removeEventListener('blur', this.handleMouseUp, true);
    }

    /**
     * 清理当前目标。
     */
    private clearTarget(): void {
        this.activeTarget = null;
        this.isDragging = false;
        this.dragPart = 'unknown';
        this.direction1Angle = MAX_ANGLE;
        this.directionReversed = false;
        this.direction2Enabled = false;
        this.direction2Angle = 0;
        if (this.ghost?.parent) {
            this.ghost.parent.remove(this.ghost);
        }
        this.ghost?.dispose();
        this.ghost = null;
        if (this.gizmo?.parent) {
            this.gizmo.parent.remove(this.gizmo);
        }
        this.gizmo?.dispose();
        this.gizmo = null;
        this.setCursor(null);
        this.requestRender();
    }

    /**
     * 请求渲染。
     */
    private requestRender(): void {
        this.context?.requestRender?.();
    }
}
